/**
 * The apps a person may connect - COW-491.
 *
 * **A curated catalogue, not the provider's.** `pipedream-connect.md` §6.1 puts
 * theirs near three thousand apps: not a dropdown, and reaching it needs the
 * connect-time credential on a route that does not exist. A short list is what
 * a picker can hold, and it can say something their catalogue cannot - whether
 * an employee has any tools for the app at all.
 *
 * Global, like the tool catalogue and for the same reason (ADR-0041): `hubspot`
 * means the same thing in every workspace.
 */
export const APP_CATEGORIES = [
  'CRM',
  'COMMUNICATION',
  'PRODUCTIVITY',
  'DEVELOPER',
  'FINANCE',
  'SUPPORT',
  'COMMERCE',
  'STORAGE',
] as const;

export type AppCategory = (typeof APP_CATEGORIES)[number];

export interface IntegrationApp {
  /**
   * The provider's own slug, which `connection.app_key` holds and
   * `GET /connect/accounts?app=` matches. Their spelling, not ours - `slack_v2`,
   * `linear_app`, `airtable_oauth` - which is exactly why a person should not
   * have to type it.
   */
  readonly appKey: string;
  readonly name: string;
  /**
   * Our grouping, and only the featured apps have one.
   *
   * The eight values are ours; Pipedream's `categories` is an array of free
   * strings and is mirrored separately. Optional since COW-492, because three
   * thousand mirrored apps have no opinion about our taxonomy.
   */
  readonly category?: AppCategory;
  readonly iconUrl?: string;
  /**
   * How many canonical capabilities are bound to this app.
   *
   * Published because **connectable and useful are different things**, and a
   * screen that conflated them would let somebody connect Dropbox and wait for
   * an employee that has nothing to do with it. Zero is a legitimate answer and
   * the app is still offerable: connecting it now and gaining tools later is
   * the ordinary path.
   */
  readonly capabilityCount: number;
}
